import { useRef, useState } from 'react'
import { Page, PageHeader } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useBasePath } from '@/lib/paths'
import { requestJson } from '@/lib/http'
import { WizardFormationRef } from '@/types'
import { ArrowRight, CheckCircle2, CircleAlert, LoaderCircle, Search } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'

type Props = {
  formation: WizardFormationRef & { entity: { suffix: string; jurisdiction_code: string } }
  backup_names: string[]
  name_suffix_options: { label: string; suffix: string }[]
  selected_name_suffix: string
}

type NameField = 'legal_name' | 'backup_name_1' | 'backup_name_2'
type NameCheckResponse = {
  local_status: 'passes' | 'issue'
  local_message: string
  registry_status: string
  registry_message: string
}
type NameCheckState = { status: 'idle' | 'checking' | 'done'; result?: NameCheckResponse }

function SuffixPreview({ id, suffix }: { id: string; suffix: string }) {
  return (
    <div
      id={id}
      className="flex min-h-9 items-center rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-foreground"
    >
      {suffix}
    </div>
  )
}

export default function NamesEdit({ formation, backup_names, name_suffix_options, selected_name_suffix }: Props) {
  const basePath = useBasePath()
  const form = useForm({
    legal_name: formation.legal_name ?? '',
    backup_name_1: backup_names[0] ?? '',
    backup_name_2: backup_names[1] ?? '',
    name_suffix: selected_name_suffix,
  })
  const [checks, setChecks] = useState<Record<NameField, NameCheckState>>({
    legal_name: { status: 'idle' },
    backup_name_1: { status: 'idle' },
    backup_name_2: { status: 'idle' },
  })
  const checkVersions = useRef<Record<NameField, number>>({ legal_name: 0, backup_name_1: 0, backup_name_2: 0 })

  const setName = (field: NameField, value: string) => {
    checkVersions.current[field] += 1
    form.setData(field, value)
    setChecks((current) => ({ ...current, [field]: { status: 'idle' } }))
  }

  const checkName = async (field: NameField) => {
    if (!form.data[field].trim()) return
    const version = ++checkVersions.current[field]
    const baseName = form.data[field]
    const nameSuffix = form.data.name_suffix
    setChecks((current) => ({ ...current, [field]: { status: 'checking' } }))
    try {
      const result = await requestJson<NameCheckResponse>(`${basePath}/formations/${formation.id}/wizard/name_check`, {
        method: 'POST',
        data: { base_name: baseName, name_suffix: nameSuffix },
      })
      if (result && version === checkVersions.current[field]) {
        setChecks((current) => ({ ...current, [field]: { status: 'done', result } }))
      }
    } catch {
      if (version !== checkVersions.current[field]) return
      setChecks((current) => ({
        ...current,
        [field]: {
          status: 'done',
          result: {
            local_status: 'issue',
            local_message: 'We could not check this name just now. You can still continue and we will check it again.',
            registry_status: 'not_checked',
            registry_message: '',
          },
        },
      }))
    }
  }

  const checkAllNames = () => Promise.all((['legal_name', 'backup_name_1', 'backup_name_2'] as NameField[]).map(checkName))

  const NameFeedback = ({ field }: { field: NameField }) => {
    const check = checks[field]
    if (check.status === 'idle') return null
    if (check.status === 'checking') {
      return <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><LoaderCircle className="size-3 animate-spin" /> Checking…</p>
    }
    if (!check.result) return null
    const passes = check.result.local_status === 'passes'
    return (
      <div className={passes ? 'text-primary' : 'text-destructive'} role="status">
        <p className="flex items-center gap-1.5 text-xs">
          {passes ? <CheckCircle2 className="size-3" /> : <CircleAlert className="size-3" />}
          {check.result.local_message}
        </p>
        {passes && check.result.registry_status === 'not_checked' && check.result.registry_message && (
          <p className="mt-1 text-xs text-muted-foreground">{check.result.registry_message}</p>
        )}
      </div>
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/name`)
  }

  const guide = (
    <WizardGuide title="How we check your name">
      <p>
        We check your name against entity-suffix rules, commonly restricted words, and our own records. Final
        availability is always confirmed by the {formation.entity.jurisdiction_code} Secretary of State when we file.
      </p>
    </WizardGuide>
  )

  return (
    <WizardLayout
      title="Name your company"
      currentStep="name"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Name your company' }]}
      guide={guide}
      mobileGuide={guide}
      tips={
        <Tip seed="names-rejection-why">
          If a name gets rejected, it's usually already taken or uses a restricted word — that's what the backups are for.
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={<>Name your company</>}
            subtitle={<>A rejected name can delay your filing — pick two backups now so we can move straight to the next one if we need to.</>}
          />

          <div className="space-y-5">
            <div className="grid gap-2 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="legal_name">Your first choice</Label>
                <Input
                  id="legal_name"
                  value={form.data.legal_name}
                  onChange={(e) => setName('legal_name', e.target.value)}
                  placeholder="Acme"
                  autoFocus
                  aria-invalid={!!form.errors.legal_name}
                  aria-describedby="name-suffix-help"
                />
                <NameFeedback field="legal_name" />
                {form.errors.legal_name && <p className="text-xs text-destructive" role="alert">{form.errors.legal_name}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="name_suffix">Legal suffix</Label>
                <Select value={form.data.name_suffix} onValueChange={(name_suffix) => {
                  checkVersions.current.legal_name += 1
                  checkVersions.current.backup_name_1 += 1
                  checkVersions.current.backup_name_2 += 1
                  form.setData('name_suffix', name_suffix)
                  setChecks({
                    legal_name: { status: 'idle' },
                    backup_name_1: { status: 'idle' },
                    backup_name_2: { status: 'idle' },
                  })
                }}>
                  <SelectTrigger id="name_suffix" className="w-full" aria-invalid={!!form.errors.name_suffix}>
                    <SelectValue placeholder="Choose a suffix">{form.data.name_suffix}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {name_suffix_options.map((option) => (
                      <SelectItem key={option.suffix} value={option.suffix}>
                        <span>{option.label}</span>
                        {option.label !== option.suffix && (
                          <span className="text-muted-foreground">({option.suffix})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.errors.name_suffix && <p className="text-xs text-destructive" role="alert">{form.errors.name_suffix}</p>}
              </div>
            </div>

            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="backup_name_1">Backup #1</Label>
                <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(10rem,max-content)]">
                  <Input
                    id="backup_name_1"
                    value={form.data.backup_name_1}
                    onChange={(e) => setName('backup_name_1', e.target.value)}
                    placeholder="Acme Holdings"
                    aria-invalid={!!form.errors.backup_name_1}
                    aria-describedby="backup-name-1-suffix name-suffix-help"
                  />
                  <SuffixPreview id="backup-name-1-suffix" suffix={form.data.name_suffix} />
                </div>
                <NameFeedback field="backup_name_1" />
                {form.errors.backup_name_1 && <p className="text-xs text-destructive" role="alert">{form.errors.backup_name_1}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="backup_name_2">Backup #2</Label>
                <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(10rem,max-content)]">
                  <Input
                    id="backup_name_2"
                    value={form.data.backup_name_2}
                    onChange={(e) => setName('backup_name_2', e.target.value)}
                    placeholder="Acme Labs"
                    aria-invalid={!!form.errors.backup_name_2}
                    aria-describedby="backup-name-2-suffix name-suffix-help"
                  />
                  <SuffixPreview id="backup-name-2-suffix" suffix={form.data.name_suffix} />
                </div>
                <NameFeedback field="backup_name_2" />
                {form.errors.backup_name_2 && <p className="text-xs text-destructive" role="alert">{form.errors.backup_name_2}</p>}
              </div>
            </div>
            <p id="name-suffix-help" className="text-sm text-muted-foreground">
              We add the selected legal suffix to all three names when we file.
            </p>
          </div>

          <WizardActions currentStep="name" formationId={formation.id} disabled={form.processing}>
              <Button
                type="button"
                variant="outline"
                onClick={() => void checkAllNames()}
                disabled={form.processing || Object.values(checks).some((check) => check.status === 'checking')}
              >
                <Search className="size-4" />
                Check names
              </Button>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? 'Saving…' : 'Continue'}
                <ArrowRight className="size-4" />
              </Button>
          </WizardActions>

        </Page>
      </form>
    </WizardLayout>
  )
}
